import React from 'react';
import { NavLink } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { AppBar, Box, CssBaseline, IconButton, Toolbar, Tooltip, Typography, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid';
import { makeStyles } from '@mui/styles';
import NavButton from 'components/Navigation/NavButton';
import Logo from 'assets/icons/Logo';
import { ReactComponent as DragAndDropActiveStateIcon } from 'assets/icons/DragNDrop_hoverState.svg';
import { jbColors } from 'theme/jbColors';
import { GlobalState } from 'reducers/stateTypes';
import { User } from 'types/User';
import { NotificationGrid } from 'components/Navigation/NotificationGrid';
import LoadingSkeleton from 'components/Navigation/LoadingSkeleton';
import { PSH_PORTAL_BACKEND_API_URL, SMALL_SCREEN_WIDTH } from 'services/constants';
import { isNotNullOrUndefined } from 'utils/utils';
import { setAccountCaseInformation } from 'reducers/caseReducer';
import { setIsDrawerOpen } from 'reducers/drawerReducer';
import { Menu } from '@mui/icons-material';
import { QuickSearch } from '@juliusbaer-gcrm/psh-search-components';

const useStyles = makeStyles(() => ({
  topBar: {
    height: '60px',
    position: 'fixed',
  },
  logo: {
    position: 'relative',
    top: '5px',
  },
  root: {
    opacity: '50%',
  },
  active: {
    opacity: '100%',
    backgroundImage: `url(${DragAndDropActiveStateIcon}) !important`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'auto',
    animation: 'none !important',
    width: '170px !important',
    '& .MuiDropzoneArea-textContainer': {
      display: 'none',
    },
  },
  dropZone: {
    marginRight: '16px',
    width: '90px',
    minHeight: '40px',
    height: '40px',
    backgroundColor: jbColors.core.reflexBlue[70],
    border: '1px dashed lightGrey',
  },
  dropZoneContainer: {
    display: 'flex',
    flexDirection: 'column-reverse',
    alignItems: 'center',
  },
  dropZoneIcon: {
    marginTop: '8px',
    width: '24px',
    height: '24px',
  },
  dropZoneParagraph: {
    margin: 0,
    marginTop: '10px',
    color: 'lightGrey',
    fontSize: '0.9rem',
  },
}));
/**
 * The top navigation bar component that holds the routed menu.
 */
const Topbar = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const user: User = useSelector((state: GlobalState) => state.user.userDetails);
  const smallScreen = useMediaQuery(`(max-width:${SMALL_SCREEN_WIDTH})`);

  return (
    <Grid container style={{ height: '60px' }} data-testid="topbar">
      <CssBaseline />
      <AppBar position="static" className={classes.topBar} elevation={0}>
        <Toolbar>
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Box display="flex" alignItems="center" overflow="auto">
              <NavLink to="/">
                <Logo />
              </NavLink>
              <Box
                style={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                  overflowX: smallScreen ? 'auto' : 'hidden',
                  width: '100%',
                  paddingRight: smallScreen ? '16px' : '0',
                  WebkitOverflowScrolling: smallScreen ? 'touch' : 'auto',
                }}
              >
                <NavButton
                  linkTo="/home"
                  onClick={() => {
                    /*Intentionally left blank*/
                  }}
                  buttonText={t('topbar.home.title')}
                />
                <NavButton
                  linkTo="/partners"
                  dataTestId="partners-link"
                  onClick={() => {
                    /*Intentionally left blank*/
                  }}
                  buttonText={t('topbar.partners.title')}
                />
                <NavButton
                  linkTo="/business-relation/edit"
                  // linkTo="/business-relation/search"
                  onClick={() => {
                    /*Intentionally left blank*/
                    dispatch(
                      setAccountCaseInformation({
                        accountNumber: '',
                        accountName: '',
                        brUid: '',
                      }),
                    );
                  }}
                  buttonText={t('topbar.businessRelations.title')}
                />
                <NavButton
                  linkTo="/document-center"
                  onClick={() => {
                    /*Intentionally left blank*/
                  }}
                  buttonText={t('topbar.documentCenter.title')}
                />
                <NavButton
                  linkTo="/gphd"
                  onClick={() => {
                    /*Intentionally left blank*/
                  }}
                  buttonText={t('topbar.gphd.title')}
                />
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              {isNotNullOrUndefined(user) ? (
                <>
                  <Box data-testid="quick-search" style={{ background: '#fff', width: '250px' }}>
                    <QuickSearch baseUrl={`${window.location.origin}${PSH_PORTAL_BACKEND_API_URL}`} />
                  </Box>

                  <Tooltip title={`Roles: ${user.roleList.join(', ')}`} placement="top" arrow>
                    <Box display="flex" flexDirection="column" alignItems="flex-end">
                      <Typography
                        variant="subtitle2"
                        style={{
                          color: '#fff',
                          fontSize: '13px',
                          paddingLeft: '24px',
                          textAlign: 'right',
                        }}
                      >
                        {`${user.givenName} ${user.sureName}`}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        style={{
                          color: '#fff',
                          fontSize: '13px',
                          paddingLeft: '24px',
                        }}
                      >
                        {user.username}
                      </Typography>
                    </Box>
                  </Tooltip>
                </>
              ) : (
                <Box display="flex" alignItems="flex-end" flexDirection="column">
                  <LoadingSkeleton width={150} />
                  <LoadingSkeleton width={75} />
                </Box>
              )}

              <NotificationGrid />
              <IconButton
                data-testid="drawer-button"
                sx={{
                  border: '1px solid white',
                  color: 'white',
                  borderRadius: 0,
                  padding: '10px',
                }}
                onClick={() => dispatch(setIsDrawerOpen(true))}
              >
                <Menu style={{ height: '16px', width: '16px' }} />
              </IconButton>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
    </Grid>
  );
};

export default Topbar;
